import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tag } from '@/components/accounting/posting-rules/status-badge';
import { Difference, Money } from './money';
import { partyHref, type TieOutApp, type TieOutParty } from './model';

interface PartiesTableProps {
  app: TieOutApp;
  parties: TieOutParty[];
}

/** Per-counterparty GL vs cari balances for the selected range. */
export function PartiesTable({ app, parties }: PartiesTableProps) {
  const { t } = useTranslation('tieOut');
  if (parties.length === 0) return <p className="px-3 py-4 text-sm text-muted-foreground">{t('parties.empty')}</p>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
            <th className="px-3 py-2 font-medium">{t('parties.columns.party')}</th>
            <th className="px-3 py-2 font-medium">{t('parties.columns.code')}</th>
            <th className="px-3 py-2 text-right font-medium">{t('parties.columns.glBalance')}</th>
            <th className="px-3 py-2 text-right font-medium">{t('parties.columns.cariBalance')}</th>
            <th className="px-3 py-2 text-right font-medium">{t('parties.columns.difference')}</th>
          </tr>
        </thead>
        <tbody>
          {parties.map((party) => (
            <tr key={`${party.kind}:${party.id}`} className="border-b border-border last:border-b-0">
              <td className="px-3 py-2">
                <Link to={partyHref(app, party)} className="font-medium hover:underline">
                  {party.name}
                </Link>
                <Tag className="ml-2">{t(`parties.kind.${party.kind}`)}</Tag>
              </td>
              <td className="px-3 py-2 font-mono text-xs">{party.cariCode ?? '—'}</td>
              <td className="px-3 py-2 text-right">
                <Money value={party.glBalance} />
              </td>
              <td className="px-3 py-2 text-right">
                <Money value={party.cariBalance} />
              </td>
              <td className="px-3 py-2 text-right">
                <Difference value={party.difference} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
